import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { ECharts } from 'echarts'
import ReactECharts from 'echarts-for-react'

import { ChartNavControls } from '@/components/charts/history/ChartNavControls'
import { buildPeriodOptions, findPeriodOption, formatPeriodLabel } from '@/components/charts/history/periodOptions'
import { useHistoryNavigation } from '@/components/charts/history/useHistoryNavigation'
import { useAuth } from '@/hooks/useAuth'
import { normalizeTimezone } from '@/insights/common/timezone'
import { formatHistoryDateTime } from '@/utils/formatDate'

import { LEGEND_WIDTH, MIN_CHART_CANVAS_PX } from './constants'
import { cursorInfoLookup } from './cursorInfo'
import { useCursorLine } from './cursorLine'
import { defaultPalette } from './palette'
import { type RowLegendEntry, rowLegendEntries } from './rowLegend'
import { belowGridHeight, buildStatusLayout } from './statusRows'
import TemperatureLegend, { type OverlayLegendInfo } from './TemperatureLegend'
import { buildGeofenceMarkAreas, buildTemperatureSeries } from './transform'
import type { AssetHistory, AssetTemperatureChartProps, DefaultHiddenSeries, LegendGroup, LegendItem } from './types'
import { LANE_LABELS_SERIES, useChartOptions } from './useChartOptions'
import { useHistoryGraphSettings } from './useHistoryGraphSettings'

import './echarts-setup'

/** The parts of an ECharts mouse event a row label hover reads: a category label of the grid
 *  under the graph (yAxis 1, value is the row key) or an in-graph lane label (by data index). */
interface LabelEventParams {
  componentType?: string
  yAxisIndex?: number
  seriesName?: string
  dataIndex?: number
  value?: unknown
  event?: { offsetX: number; offsetY: number }
}

function matchesHidden(item: LegendItem, hidden?: DefaultHiddenSeries): boolean {
  if (!hidden) return false
  if (hidden.labels?.includes(item.label)) return true
  return hidden.namePattern?.test(item.name) ?? false
}

/** Merge prior selections with defaults for any newly-introduced legend items.
 *  Series names are zone-prefixed (e.g. "Zone 1 Return"), so prior selections
 *  for hidden zones survive a tab switch and reappear when the user comes back. */
function mergeSelected(
  groups: LegendGroup[],
  prev: Record<string, boolean>,
  defaultHidden?: DefaultHiddenSeries
): Record<string, boolean> {
  const next: Record<string, boolean> = { ...prev }
  for (const g of groups) {
    for (const item of g.items) {
      if (!(item.name in next)) {
        next[item.name] = !(item.defaultOff || matchesHidden(item, defaultHidden))
      }
    }
  }
  return next
}

/** Filter AssetHistory to only include a single zone's data (+ common data like ambient, power, etc.) */
function filterDataForZone(data: AssetHistory, zoneName: string): AssetHistory {
  const zones: AssetHistory['zones'] = {}
  if (data.zones[zoneName]) {
    zones[zoneName] = data.zones[zoneName]
  }

  // Filter monitoring points to only those belonging to this zone
  const monitoringPoints: AssetHistory['monitoringPoints'] = {}
  for (const [mpName, samples] of Object.entries(data.monitoringPoints)) {
    if (!samples?.length) continue
    if (samples[0].zoneName === zoneName) {
      monitoringPoints[mpName] = samples
    }
  }

  return {
    ...data,
    zones,
    monitoringPoints,
  }
}

function AssetHistoryChart({
  data,
  reload,
  from,
  periodHours,
  units,
  alertMarkers,
  chartHeight = 500,
  hideNavigation = false,
  externalLoading = false,
  defaultHidden,
  temperatureLimits,
  flattenZoneLegend,
  hideGeofences = false,
  initialWindow,
  onWindowChange,
  onViewChange,
  leading,
}: AssetTemperatureChartProps) {
  const chartRef = useRef<ReactECharts>(null)
  const zone = normalizeTimezone(useAuth().user?.timezone)
  const [activeTab, setActiveTab] = useState<string | null>(null)

  // `chartHeight` is a total budget for this component, not the canvas height. Measure
  // the nav bar (its height varies with the range pickers and period selector) and give
  // the canvas whatever is left, so callers never have to guess the nav's size.
  const [navHeight, setNavHeight] = useState(0)
  const navObserverRef = useRef<ResizeObserver | null>(null)
  const setNavContainer = useCallback((el: HTMLDivElement | null) => {
    navObserverRef.current?.disconnect()
    navObserverRef.current = null
    if (!el || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver((entries) => {
      setNavHeight(Math.ceil(entries[0]?.contentRect.height ?? 0))
    })
    observer.observe(el)
    navObserverRef.current = observer
  }, [])
  useEffect(() => () => navObserverRef.current?.disconnect(), [])

  // Shared window state machine: range (fetched) vs view (drawn), reload reconciliation.
  const {
    currentData,
    loading,
    periodWindow,
    range,
    viewHours,
    isModified,
    navigate,
    changeViewHours,
    applyRange,
    jumpToDate,
    resetToDefault,
  } = useHistoryNavigation<AssetHistory>({
    data,
    from,
    periodHours,
    zone,
    reload,
    initialWindow,
    onWindowChange,
    onViewChange,
  })

  // Zone names: union of reefer zones (data.zones) + any zoneName referenced by
  // monitoring point samples. An MP can belong to a zone that has no reefer data,
  // in which case the zone wouldn't otherwise get a tab.
  const zoneNames = useMemo(() => {
    if (!currentData) return []
    const fromZones = Object.keys(currentData.zones).filter((k) => currentData.zones[k] != null)
    const result = [...fromZones]
    for (const samples of Object.values(currentData.monitoringPoints)) {
      const z = samples?.[0]?.zoneName
      if (z && !result.includes(z)) result.push(z)
    }
    return result
  }, [currentData])

  const showTabs = zoneNames.length >= 2

  // Initialize activeTab on first data load; afterwards preserve the user's selection
  // across navigation even if the new period's data doesn't include that zone (the
  // tab will reappear when the user navigates back to a period that has it).
  useEffect(() => {
    if (activeTab === null && zoneNames.length > 0) {
      setActiveTab(zoneNames[0])
    }
  }, [zoneNames, activeTab])

  // Filter data based on active tab
  const displayData = useMemo(() => {
    if (!currentData) return undefined
    if (!activeTab || activeTab === 'all' || !showTabs) return currentData
    return filterDataForZone(currentData, activeTab)
  }, [currentData, activeTab, showTabs])

  const periodOptions = buildPeriodOptions(range)
  const selectedPeriod = findPeriodOption(periodOptions, viewHours)
  const periodLabel = selectedPeriod?.label ?? formatPeriodLabel(viewHours)

  // Track whether the user has zoomed in (ref avoids re-render loops with notMerge)
  const [isZoomed, setIsZoomed] = useState(false)
  const zoomTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Reset zoom state when period window changes (e.g. navigation)
  useEffect(() => {
    setIsZoomed(false)
  }, [periodWindow])

  // Clean up debounce timer on unmount
  useEffect(
    () => () => {
      if (zoomTimer.current) clearTimeout(zoomTimer.current)
    },
    []
  )

  // Build temperature series + legend groups from filtered data
  const singleZoneView = !!activeTab && activeTab !== 'all' && showTabs
  const tempResult = useMemo(
    () =>
      displayData
        ? buildTemperatureSeries(
            displayData,
            0,
            0,
            defaultPalette,
            units,
            singleZoneView,
            temperatureLimits,
            flattenZoneLegend
          )
        : undefined,
    [displayData, units, singleZoneView, temperatureLimits, flattenZoneLegend]
  )

  // Legend selected state — preserved across tab switches (series names are zone-prefixed,
  // so a hidden zone's selections sit dormant in state and reappear when the user returns)
  const [legendSelected, setLegendSelected] = useState<Record<string, boolean>>({})
  useEffect(() => {
    if (tempResult) {
      setLegendSelected((prev) => mergeSelected(tempResult.legendGroups, prev, defaultHidden))
    }
  }, [tempResult])

  const handleToggle = useCallback((name: string) => {
    // Default-true semantics: an absent key reads as visible everywhere else
    // (selected[name] ?? true), so the first toggle of an overlay must flip
    // from true→false, not from undefined→true (which looks like a no-op).
    setLegendSelected((prev) => ({ ...prev, [name]: !(prev[name] ?? true) }))
  }, [])

  const handleToggleGroup = useCallback(
    (groupName: string) => {
      if (!tempResult) return
      const group = tempResult.legendGroups.find((g) => g.groupName === groupName)
      if (!group) return
      // If all are on, turn all off; otherwise turn all on
      const allOn = group.items.every((i) => legendSelected[i.name] ?? true)
      const newValue = !allOn
      setLegendSelected((prev) => {
        const next = { ...prev }
        for (const item of group.items) {
          next[item.name] = newValue
        }
        return next
      })
    },
    [tempResult, legendSelected]
  )

  const overlayLegend = useMemo<OverlayLegendInfo | undefined>(() => {
    if (!displayData) return undefined
    const hasDoorOpen = (displayData.doorEvents ?? []).some((e) => e.status === 'open')
    const hasPower = (displayData.powerState ?? []).length > 0
    const hasMovement = (displayData.movement ?? []).length > 0
    const geofenceResult = buildGeofenceMarkAreas(hideGeofences ? [] : displayData.location)
    const hasAlertPins = (displayData.alertPins ?? []).length > 0
    return {
      hasDoorOpen,
      hasPower,
      hasMovement,
      hasAlertPins,
      // Match the (darker) hatch colors drawn in the chart body so the legend swatches agree.
      doorColorOutside: '#b91c1c',
      doorColorInside: '#1d4ed8',
      powerColor: '#16a34a',
      movementColor: '#000000',
      geofences: geofenceResult.legendEntries,
      doorsByGeofence: !hideGeofences,
    }
  }, [displayData, hideGeofences])

  // Which status lanes sit in the graph and which rows go under it (with their section
  // headers). The rows under the graph add a second grid; grow the container by its height
  // so the temperature area keeps its size instead of being squished.
  const { statusBarsBelow, collapsedSections, setStatusBarsBelow, toggleSection } = useHistoryGraphSettings()
  const statusLayout = useMemo(
    () => (displayData ? buildStatusLayout(displayData, statusBarsBelow, collapsedSections, hideGeofences) : undefined),
    [displayData, statusBarsBelow, collapsedSections, hideGeofences]
  )
  const extraHeight = belowGridHeight(statusLayout?.below.length ?? 0)
  const canvasHeight = Math.max(MIN_CHART_CANVAS_PX, chartHeight - navHeight)
  const containerHeight = canvasHeight + extraHeight

  // echarts-for-react only resizes on window resize, so a change to the element's
  // style height leaves the canvas painting at its previous size (overflowing the
  // nav bar below it). Resize the instance whenever the box changes.
  useEffect(() => {
    chartRef.current?.getEchartsInstance()?.resize()
  }, [containerHeight])

  const hasAnyLegendContent =
    (tempResult?.legendGroups.length ?? 0) > 0 ||
    !!overlayLegend?.hasDoorOpen ||
    !!overlayLegend?.hasPower ||
    !!overlayLegend?.hasMovement ||
    (overlayLegend?.geofences.length ?? 0) > 0 ||
    !!overlayLegend?.hasAlertPins

  const options = useChartOptions(
    displayData,
    tempResult,
    legendSelected,
    {},
    [],
    periodWindow,
    units,
    zone,
    alertMarkers,
    temperatureLimits,
    showTabs,
    hideGeofences,
    statusLayout
  )

  // A click on a section header's label (category axis of the grid under the graph) expands
  // or collapses that section. Read through a ref so onEvents stays stable: echarts-for-react
  // rebinds every handler whenever the object changes.
  const sectionClickRef = useRef<(key: string) => void>(() => undefined)
  sectionClickRef.current = (key: string) => {
    const row = statusLayout?.below.find((r) => r.key === key)
    if (row?.header) toggleSection(row.section)
  }

  // Hovering a status row's label shows its color key, as Refresh does: the category labels
  // of the grid under the graph and the in-graph lane labels both raise it. Through a ref
  // for the same reason as sectionClickRef.
  const [rowLegend, setRowLegend] = useState<{
    title: string
    entries: RowLegendEntry[]
    x: number
    y: number
  } | null>(null)
  const rowLabelHoverRef = useRef<(params: LabelEventParams, over: boolean) => void>(() => undefined)
  rowLabelHoverRef.current = (params, over) => {
    const row =
      params.componentType === 'yAxis' && params.yAxisIndex === 1
        ? statusLayout?.below.find((r) => r.key === params.value)
        : params.componentType === 'series' && params.seriesName === LANE_LABELS_SERIES && params.dataIndex != null
          ? statusLayout?.inGraph[params.dataIndex]
          : undefined
    if (!row) return
    const entries = over ? rowLegendEntries(defaultPalette, row.key) : null
    setRowLegend(
      entries ? { title: row.label, entries, x: params.event?.offsetX ?? 0, y: params.event?.offsetY ?? 0 } : null
    )
  }

  // The cursor popover: the time under the vertical line, the nearest address and any geofence.
  const cursorInfoAt = useMemo(
    () => (displayData ? cursorInfoLookup(displayData, hideGeofences) : undefined),
    [displayData, hideGeofences]
  )
  const describeCursor = useCallback(
    (time: number): string[] => {
      if (!cursorInfoAt) return []
      const { address, geofences } = cursorInfoAt(time)
      return [
        formatHistoryDateTime(time, zone),
        ...(address ? [address] : []),
        ...geofences.map((name) => `Geofence: ${name}`),
      ]
    },
    [cursorInfoAt, zone]
  )
  const {
    lineRef: cursorLineRef,
    popoverRef: cursorPopoverRef,
    attach: attachCursorLine,
  } = useCursorLine(describeCursor)

  const resetZoom = useCallback(() => {
    const instance = chartRef.current?.getEchartsInstance()
    if (instance) {
      instance.dispatchAction({ type: 'dataZoom', dataZoomIndex: 0, start: 0, end: 100 })
    }
    setIsZoomed(false)
  }, [])

  const onEvents = useMemo(
    () => ({
      datazoom: (params: { batch?: { start?: number; end?: number }[]; start?: number; end?: number }) => {
        const batch = params.batch?.[0] ?? params
        if (batch.start != null && batch.end != null) {
          const zoomed = batch.start > 0.5 || batch.end < 99.5
          if (zoomTimer.current) clearTimeout(zoomTimer.current)
          zoomTimer.current = setTimeout(() => {
            setIsZoomed(zoomed)
          }, 200)
        }
      },
      mouseover: (params: LabelEventParams) => rowLabelHoverRef.current(params, true),
      mouseout: (params: LabelEventParams) => rowLabelHoverRef.current(params, false),
      click: (params: { componentType?: string; yAxisIndex?: number; value?: unknown }) => {
        if (params.componentType === 'yAxis' && params.yAxisIndex === 1 && typeof params.value === 'string') {
          sectionClickRef.current(params.value)
        }
      },
      dblclick: () => {
        const instance = chartRef.current?.getEchartsInstance()
        if (instance) {
          instance.dispatchAction({ type: 'dataZoom', dataZoomIndex: 0, start: 0, end: 100 })
        }
        setIsZoomed(false)
      },
    }),
    []
  )

  const zoneButtons = showTabs && (
    <div className="flex gap-1">
      {zoneNames.map((name) => (
        <button
          key={name}
          onClick={() => setActiveTab(name)}
          className={`px-3 h-8 text-xs font-medium rounded transition-colors ${
            activeTab === name ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          {name}
        </button>
      ))}
      <button
        onClick={() => setActiveTab('all')}
        className={`px-3 h-8 text-xs font-medium rounded transition-colors ${
          activeTab === 'all' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
        }`}
      >
        All
      </button>
    </div>
  )

  const zoneTabs = leading ? (
    <div className="flex flex-wrap items-center gap-2">
      {leading}
      {zoneButtons}
    </div>
  ) : (
    zoneButtons
  )

  return (
    <div>
      <div ref={hideNavigation ? undefined : setNavContainer} className="flex flex-col gap-2 px-4">
        {hideNavigation && zoneTabs}

        {!hideNavigation && (
          <ChartNavControls
            leading={zoneTabs}
            periodLabel={periodLabel}
            periodWindow={periodWindow}
            range={range}
            from={from}
            zone={zone}
            isModified={isModified}
            isZoomed={isZoomed}
            onResetZoom={resetZoom}
            navigate={navigate}
            jumpToDate={jumpToDate}
            resetToDefault={resetToDefault}
            showRange
            onRangeChange={applyRange}
          >
            <select
              className="select select-xs w-fit text-xs"
              value={selectedPeriod?.value ?? String(viewHours)}
              onChange={(e) => changeViewHours(Number(e.target.value))}
              title="Period"
            >
              {periodOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </ChartNavControls>
        )}
      </div>
      <div className="flex" style={{ minHeight: containerHeight }}>
        {tempResult && hasAnyLegendContent ? (
          <TemperatureLegend
            groups={tempResult.legendGroups}
            selected={legendSelected}
            onToggle={handleToggle}
            onToggleGroup={handleToggleGroup}
            overlays={overlayLegend}
            statusBars={
              statusLayout?.hasMovableLanes ? { below: statusBarsBelow, onChange: setStatusBarsBelow } : undefined
            }
          />
        ) : (
          <div className="shrink-0 pl-2 pt-2 text-xs italic text-gray-500" style={{ width: LEGEND_WIDTH }}>
            No data for period
          </div>
        )}
        <div className="flex-1 min-w-0 relative">
          <ReactECharts
            ref={chartRef}
            option={options}
            notMerge={true}
            lazyUpdate={true}
            onEvents={onEvents}
            onChartReady={(chart: ECharts) => attachCursorLine(chart)}
            opts={{ width: 'auto', height: 'auto' }}
            style={{ height: containerHeight, width: '100%' }}
          />
          {/* Follows the pointer over the graph and the rows under it; pointer-events none so
              every tooltip underneath still works. */}
          <div
            ref={cursorLineRef}
            className="pointer-events-none absolute w-px bg-gray-500"
            style={{ display: 'none', top: 0, left: 0 }}
          />
          <div
            ref={cursorPopoverRef}
            className="pointer-events-none absolute z-10 whitespace-nowrap rounded border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 shadow-md"
            style={{ display: 'none', top: 0, left: 0 }}
          />
          {rowLegend && (
            <div
              className="pointer-events-none absolute z-10 rounded border border-gray-200 bg-white p-1.5 text-xs shadow-lg"
              style={{ left: rowLegend.x + 12, top: rowLegend.y + 12 }}
            >
              <div className="mb-1 font-medium text-gray-700">{rowLegend.title}</div>
              {rowLegend.entries.map((entry) => (
                <div key={entry.key} className="flex items-center gap-1.5 py-0.5">
                  <span className="inline-block h-3 w-3 shrink-0 rounded-sm" style={{ backgroundColor: entry.color }} />
                  <span className="text-gray-600">{entry.label}</span>
                </div>
              ))}
            </div>
          )}
          {(loading || externalLoading) && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/60">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600" />
            </div>
          )}
        </div>
      </div>

      {/* Navigation buttons: prev / next / date picker centered as a fixed set */}
    </div>
  )
}

export default memo(AssetHistoryChart)
